import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import CourseCard from '../components/Coursecard';
import SocialLinks from '../components/SocialLinks';
import TrainingJourney from '../components/lux/TrainingJourney';
import LearnResources from '../components/lux/LearnResources';
import LuxCta from '../components/lux/LuxCta';
import { HERO_VIDEO } from '../data/social';

const stats = [
  { value: '500+', label: 'Students Placed' },
  { value: '100%', label: 'Placement Support' },
  { value: '16+', label: 'Programs' },
  { value: '200+', label: 'Partners' },
];

const featuredCourses = [
  { title: 'PLC / SCADA Full Course', duration: '3 Months', price: '₹24,999', features: ['100% Lab Practical', 'Industry Projects', 'Placement Support'], img: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800', badge: 'Top Pick' },
  { title: 'Industrial Automation', duration: '3 Months', price: '₹24,999', features: ['Real PLC Panels', 'Hands-on Tools', 'Certification'], img: 'https://leantransitionsolutions.com/assets/img/blog/Industrial_Automation.jpg' },
  { title: 'SAP Full Course', duration: '3 Months', price: '₹24,999', features: ['Job Oriented', 'Live Cases', 'Placement'], img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800', badge: 'Placement' },
  { title: 'Full Stack Developer', duration: '6 Months', price: '₹24,999', features: ['MERN Stack', 'Portfolio', 'Placement'], img: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800', badge: 'Popular' },
];

const labPhotos = ['/pl2.jpeg', '/pl3.jpg', '/pl7.jpg', '/pl8.jpg', '/pl10.jpg', '/pl11.jpg'];

function AnimatedStat({ target }) {
  const [display, setDisplay] = useState(target);
  const ref = useRef();
  useEffect(() => {
    const num = parseInt(target.replace(/\D/g, ''), 10);
    const suffix = target.replace(/[0-9]/g, '');
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      let s = 0;
      const step = Math.ceil(num / 50);
      const t = setInterval(() => {
        s = Math.min(s + step, num);
        setDisplay(`${s}${suffix}`);
        if (s >= num) clearInterval(t);
      }, 30);
      obs.disconnect();
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref}>{display}</span>;
}

export default function Home() {
  const statementRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: statementRef, offset: ['start end', 'end start'] });
  const statementY = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <div className="home-lux">
      <section className="cine-hero">
        <div className="cine-hero__media">
          <video className="cine-hero__video" autoPlay muted loop playsInline poster="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1920">
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
        </div>
        <div className="cine-hero__overlay" />
        <div className="cine-hero__content">
          <motion.span className="cine-hero__label" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}>
            Noida · Industrial Training
          </motion.span>
          <motion.h1 className="cine-hero__title" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}>
            Revealing
            <em>Hands-On Craft</em>
          </motion.h1>
          <motion.p className="cine-hero__sub" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.7 }}>
            PLC, SCADA and industrial automation — train on real equipment with mentors who build careers.
          </motion.p>
          <motion.div className="cine-hero__actions" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.95 }}>
            <Link to="/admission" className="btn-lux btn-lux--fill">Discover Programs</Link>
            <Link to="/courses" className="btn-lux btn-lux--line">All Courses</Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} style={{ marginTop: 28 }}>
            <SocialLinks size="sm" />
          </motion.div>
        </div>
        <a href="#story" className="cine-hero__scroll" aria-label="Scroll">Scroll<span className="cine-hero__scroll-line" /></a>
      </section>

      <div className="lux-stats">
        {stats.map(s => (
          <div key={s.label} className="lux-stats__cell">
            <div className="lux-stats__num"><AnimatedStat target={s.value} /></div>
            <div className="lux-stats__label">{s.label}</div>
          </div>
        ))}
      </div>

      <section id="story" ref={statementRef} className="lux-statement">
        <motion.div style={{ y: statementY }}>
          <p className="lux-statement__eyebrow">Since 2018 · Sector-3 Noida</p>
          <p className="lux-statement__text">
            We craft <em>industry-ready engineers</em> through practical training on live PLC panels and automation labs.
          </p>
        </motion.div>
      </section>

      <section>
        <div className="video-gallery__header container-wide" style={{ marginBottom: 24 }}>
          <p className="lux-statement__eyebrow">Inside the institute</p>
          <h2 className="video-gallery__title">The workshop <em style={{ fontStyle: 'italic', color: 'var(--accent-soft)' }}>in motion</em></h2>
        </div>
        <div className="lux-gallery-strip">
          {labPhotos.map((src, i) => (
            <motion.div key={src} className="lux-gallery-strip__img" initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: i * 0.08 }}>
              <img src={src} alt={`Lab ${i + 1}`} loading="lazy" />
            </motion.div>
          ))}
        </div>
      </section>

      <TrainingJourney />
      <LearnResources />

      <section className="h-scroll">
        <div className="h-scroll__head container-wide">
          <p className="lux-statement__eyebrow">Our 2026 programs</p>
          <h2 className="video-gallery__title">Explore <em style={{ fontStyle: 'italic' }}>programs</em></h2>
        </div>
        <div className="h-scroll__track">
          {featuredCourses.map((c, i) => (
            <motion.div key={c.title} className={`h-scroll__item ${i === 0 ? 'h-scroll__item--wide' : ''}`} initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
              <CourseCard {...c} />
            </motion.div>
          ))}
          <motion.div className="h-scroll__item" whileInView={{ opacity: 1 }} viewport={{ once: true }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320, border: '1px solid var(--border)', background: 'var(--surface)' }}>
            <Link to="/courses" className="btn-lux btn-lux--line" style={{ color: 'var(--text)' }}>View all 16+ courses</Link>
          </motion.div>
        </div>
      </section>

      <LuxCta title="Your career begins" highlight="here" secondary={{ to: '/contact', label: 'Contact' }} />
    </div>
  );
}
